import { useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, FileText, Calendar, Plus, Trash2, ExternalLink } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

const addFeedbackFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  day: z.coerce.number().min(1, "Day must be at least 1"),
  formUrl: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  isActive: z.boolean().default(true),
});

type AddFeedbackFormData = z.infer<typeof addFeedbackFormSchema>;

export default function AddFeedbackForm() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Fetch existing feedback forms to check for conflicts
  const { data: existingForms } = useQuery({
    queryKey: ['/feedback-forms'],
  }) as { data: { forms: any[] } | undefined };

  const form = useForm<AddFeedbackFormData>({
    resolver: zodResolver(addFeedbackFormSchema),
    defaultValues: {
      title: "",
      description: "",
      day: 1,
      formUrl: "",
      isActive: true,
    },
  });

  // Function to check if day already has an active form
  const isDayTaken = (day: number) => {
    if (!existingForms?.forms) return false;
    return existingForms.forms.some((form: any) => form.day === day && form.isActive);
  };

  // Function to get next available day
  const getNextAvailableDay = () => {
    if (!existingForms?.forms) return 1;
    
    const takenDays = existingForms.forms
      .filter((form: any) => form.isActive)
      .map((form: any) => form.day);
    
    for (let day = 1; day <= 30; day++) {
      if (!takenDays.includes(day)) {
        return day;
      }
    }
    return 1;
  };


  const createFeedbackFormMutation = useMutation({
    mutationFn: async (data: AddFeedbackFormData) => {
      // Send formUrl as-is since schema expects formUrl (camelCase)
      const payload = {
        ...data,
        formUrl: data.formUrl || null,
      };
      
      const response = await apiRequest("POST", "/feedback-forms", payload);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Feedback form has been created successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['/feedback-forms'] });
      setLocation('/feedback');
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: AddFeedbackFormData) => {
    // Check if day is already taken
    if (isDayTaken(data.day)) {
      toast({
        title: "Day Already Taken",
        description: `Day ${data.day} already has an active feedback form. Please choose a different day.`,
        variant: "destructive",
      });
      return;
    }

    createFeedbackFormMutation.mutate(data);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="text-page-title">
              Add Feedback Form
            </h1>
            <p className="text-gray-600 mt-1" data-testid="text-page-description">
              Create a new day-wise feedback form for employees
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setLocation('/feedback')}
            className="flex items-center space-x-2"
            data-testid="button-back-to-feedback"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Feedback</span>
          </Button>
        </div>

        <Card className="border-gray-200 shadow-lg">
          <CardHeader className="border-b border-gray-200">
            <CardTitle className="flex items-center">
              <FileText className="mr-2 h-5 w-5 text-servify-purple" />
              Feedback Form Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="feedback-form">
                
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Title */}
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base font-semibold text-gray-900">Title</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter feedback form title"
                            className="border-gray-300 focus:border-servify-purple"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Day */}
                  <FormField
                    control={form.control}
                    name="day"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base font-semibold text-gray-900">Day</FormLabel>
                        <FormControl>
                          <div className="flex items-center space-x-2">
                            <Input
                              type="number"
                              min="1"
                              placeholder="1"
                              className="border-gray-300 focus:border-servify-purple"
                              {...field}
                              onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => field.onChange(getNextAvailableDay())}
                              className="whitespace-nowrap"
                            >
                              Next Available
                            </Button>
                          </div>
                        </FormControl>
                        {isDayTaken(field.value) && (
                          <p className="text-sm text-red-600">Day {field.value} already has an active form</p>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold text-gray-900">Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe the purpose and context of this feedback form"
                          className="min-h-[100px] border-gray-300 focus:border-servify-purple"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* External Form URL */}
                <FormField
                  control={form.control}
                  name="formUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold text-gray-900">External Form URL (Optional)</FormLabel>
                      <FormControl>
                        <div className="flex items-center space-x-2">
                          <Input
                            placeholder="https://forms.google.com/..."
                            className="border-gray-300 focus:border-servify-purple"
                            {...field}
                          />
                          {field.value && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => window.open(field.value, '_blank')}
                              className="flex items-center space-x-1"
                            >
                              <ExternalLink className="h-4 w-4" />
                              <span>Test</span>
                            </Button>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Day-wise Form Information */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-900">Day-wise Feedback Form</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        This form will be unlocked for employees on the specified day based on their joining date. 
                        Use the external URL to link to your custom feedback form or survey.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-4 pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLocation('/feedback')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-servify-purple hover:bg-servify-purple/90 text-white"
                    disabled={createFeedbackFormMutation.isPending}
                    data-testid="submit-feedback-form"
                  >
                    {createFeedbackFormMutation.isPending ? "Creating..." : "Create Feedback Form"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}